import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { orderService } from '../../services/orderService';
import { driverService } from '../../services/driverService';
import OrderStatusBadge from '../../components/orders/OrderStatusBadge';
import ManualAssignmentModal from '../../components/orders/ManualAssignmentModal';
import CancelOrderModal from '../../components/orders/CancelOrderModal';
import {
    ChevronLeft,
    MapPin,
    User,
    Car,
    Clock,
    DollarSign,
    MessageSquare,
    Phone,
    AlertTriangle
} from 'lucide-react';

const OrderDetailPage = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [orderDetail, setOrderDetail] = useState(null);
    const [orderTracking, setOrderTracking] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [nearbyDrivers, setNearbyDrivers] = useState([]);

    useEffect(() => {
        fetchOrderDetail();
    }, [orderId]);

    const fetchOrderDetail = async () => {
        setIsLoading(true);
        try {
            const orderData = await orderService.getOrderById(orderId);
            setOrderDetail(orderData);

            if (['pending', 'accepted'].includes(orderData.status)) {
                fetchNearbyDrivers(orderData.pickup_latitude, orderData.pickup_longitude);
            }

            if (['accepted', 'driver_arriving', 'in_progress'].includes(orderData.status)) {
                fetchOrderTracking();
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrderTracking = async () => {
        try {
            const trackingData = await orderService.getOrderTracking(orderId);
            setOrderTracking(trackingData);
        } catch (error) {
            console.error('Error fetching order tracking:', error);
        }
    };

    const fetchNearbyDrivers = async (latitude, longitude) => {
        try {
            const driversData = await driverService.getNearbyDrivers(latitude, longitude, 5, orderDetail?.fleet_id);
            setNearbyDrivers(driversData);
        } catch (error) {
            console.error('Error fetching nearby drivers:', error);
        }
    };

    const handleManualAssignment = async (driverId) => {
        try {
            await orderService.assignDriver(orderId, {
                driver_id: driverId,
                dispatcher_id: user.dispatcher_id
            });

            setShowAssignModal(false);
            fetchOrderDetail(); // 重新加載訂單詳情
        } catch (error) {
            console.error('Error assigning driver:', error);
        }
    };

    const handleCancelOrder = async (reason) => {
        try {
            await orderService.cancelOrder(orderId, {
                cancelled_by: 'dispatcher',
                cancellation_reason: reason
            });

            setShowCancelModal(false);
            fetchOrderDetail(); // 重新加載訂單詳情
        } catch (error) {
            console.error('Error cancelling order:', error);
        }
    };

    const handleGlobalDispatch = async () => {
        try {
            // 這裡假設有一個API啟用全局派單模式
            await orderService.enableGlobalDispatch(orderId);
            fetchOrderDetail(); // 重新加載訂單詳情
        } catch (error) {
            console.error('Error enabling global dispatch:', error);
        }
    };

    const getStatusTimestamp = (status) => {
        if (!orderDetail) return null;

        switch (status) {
            case 'accepted':
                return orderDetail.accepted_at;
            case 'in_progress':
                return orderDetail.started_at;
            case 'completed':
                return orderDetail.completed_at;
            case 'cancelled':
                return orderDetail.cancelled_at;
            default:
                return orderDetail.created_at;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!orderDetail) {
        return (
            <div className="text-center py-12">
                <h2 className="text-xl font-bold text-gray-800 mb-2">找不到訂單</h2>
                <p className="text-gray-600 mb-4">無法找到ID為 {orderId} 的訂單資訊。</p>
                <button
                    onClick={() => navigate('/orders')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                    返回訂單列表
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <button
                    onClick={() => navigate('/orders')}
                    className="p-2 mr-2 rounded-md hover:bg-gray-100"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold">訂單詳情</h1>
                <OrderStatusBadge status={orderDetail.status} className="ml-4" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 訂單資訊卡片 */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                        <div className="flex justify-between items-center mb-2">
                            <h4 className="font-bold">訂單狀態</h4>
                            <OrderStatusBadge status={orderDetail.status} />
                        </div>
                        <p className="text-sm text-gray-600">
                            訂單編號：{orderDetail.order_number}<br />
                            建立時間：{new Date(orderDetail.created_at).toLocaleString('zh-TW')}
                        </p>
                    </div>

                    <div className="p-4 border-b border-gray-100">
                        <h4 className="font-bold mb-3">行程資訊</h4>
                        <div className="flex items-start mb-4">
                            <div className="flex flex-col items-center mr-3">
                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                <div className="w-0.5 h-16 bg-gray-300 my-1"></div>
                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            </div>
                            <div>
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600">上車地點</p>
                                    <p className="font-medium">{orderDetail.pickup_address}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">下車地點</p>
                                    <p className="font-medium">{orderDetail.dropoff_address}</p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div className="bg-gray-50 p-2 rounded-md">
                                <p className="text-xs text-gray-600">預估距離</p>
                                <p className="font-medium">{orderDetail.estimated_distance} 公里</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-md">
                                <p className="text-xs text-gray-600">預估時間</p>
                                <p className="font-medium">{orderDetail.estimated_duration} 分鐘</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded-md">
                                <p className="text-xs text-gray-600">
                                    {orderDetail.status === 'completed' ? '實際車資' : '預估車資'}
                                </p>
                                <p className="font-medium">
                                    ${orderDetail.status === 'completed' ? orderDetail.actual_fare : orderDetail.estimated_fare}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-4 border-b border-gray-100">
                        <h4 className="font-bold mb-3">乘客資訊</h4>
                        <div className="flex items-center">
                            <div className="w-12 h-12 bg-gray-200 rounded-full mr-3"></div>
                            <div>
                                <p className="font-medium">{orderDetail.passenger_name}</p>
                                <p className="text-sm text-gray-600">{orderDetail.passenger_phone}</p>
                            </div>
                            <div className="ml-auto flex space-x-2">
                                <button className="bg-blue-100 text-blue-600 p-2 rounded-full">
                                    <MessageSquare className="w-5 h-5" />
                                </button>
                                <button className="bg-green-100 text-green-600 p-2 rounded-full">
                                    <Phone className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {orderDetail.driver_id && (
                        <div className="p-4 border-b border-gray-100">
                            <h4 className="font-bold mb-3">司機資訊</h4>
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-gray-200 rounded-full mr-3"></div>
                                <div>
                                    <p className="font-medium">{orderDetail.driver_name}</p>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Car className="w-4 h-4 mr-1" />
                                        <span>{orderDetail.vehicle_model} • {orderDetail.plate_number}</span>
                                    </div>
                                </div>
                                <div className="ml-auto flex space-x-2">
                                    <button className="bg-blue-100 text-blue-600 p-2 rounded-full">
                                        <MessageSquare className="w-5 h-5" />
                                    </button>
                                    <button className="bg-green-100 text-green-600 p-2 rounded-full">
                                        <Phone className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {orderDetail.cancelled_by && (
                        <div className="p-4 border-b border-gray-100">
                            <h4 className="font-bold mb-3 text-red-600">取消原因</h4>
                            <div className="bg-red-50 p-3 rounded-md">
                                <div className="flex items-start">
                                    <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                                    <div>
                                        <p className="text-sm text-red-700">
                                            訂單被 {
                                                {
                                                    'passenger': '乘客',
                                                    'driver': '司機',
                                                    'dispatcher': '調度員',
                                                    'system': '系統'
                                                }[orderDetail.cancelled_by] || orderDetail.cancelled_by
                                            } 取消
                                        </p>
                                        <p className="text-sm text-gray-600 mt-1">{orderDetail.cancellation_reason}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {orderDetail.note && (
                        <div className="p-4">
                            <h4 className="font-bold mb-3">備註</h4>
                            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                {orderDetail.note}
                            </p>
                        </div>
                    )}
                </div>

                {/* 右側操作卡片 */}
                <div className="lg:col-span-1 space-y-6">
                    {/* 操作按鈕 */}
                    {orderDetail.status === 'pending' && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold mb-3">訂單操作</h4>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setShowAssignModal(true)}
                                    className="w-full bg-blue-500 text-white py-2 rounded-md font-medium"
                                >
                                    手動派單
                                </button>
                                <button
                                    onClick={handleGlobalDispatch}
                                    className="w-full bg-purple-500 text-white py-2 rounded-md font-medium"
                                >
                                    啟用全局派單
                                </button>
                                <button
                                    onClick={() => setShowCancelModal(true)}
                                    className="w-full border border-red-500 text-red-500 py-2 rounded-md font-medium"
                                >
                                    取消訂單
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 訂單時間線 */}
                    {!['cancelled', 'pending'].includes(orderDetail.status) && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold mb-3">訂單進度</h4>
                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="mr-3 relative">
                                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">
                                            1
                                        </div>
                                        <div className="w-0.5 h-14 bg-gray-300 absolute left-1/2 top-6 transform -translate-x-1/2"></div>
                                    </div>
                                    <div>
                                        <p className="font-medium">訂單建立</p>
                                        <p className="text-sm text-gray-600">
                                            {new Date(orderDetail.created_at).toLocaleString('zh-TW')}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="mr-3 relative">
                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${orderDetail.status === 'pending' ? 'bg-gray-300 text-gray-600' : 'bg-blue-500 text-white'
                                            }`}>
                                            2
                                        </div>
                                        {orderDetail.status !== 'pending' && orderDetail.status !== 'accepted' && (
                                            <div className="w-0.5 h-14 bg-gray-300 absolute left-1/2 top-6 transform -translate-x-1/2"></div>
                                        )}
                                    </div>
                                    <div>
                                        <p className={`font-medium ${orderDetail.status === 'pending' ? 'text-gray-400' : ''}`}>
                                            司機接單
                                        </p>
                                        {orderDetail.accepted_at && (
                                            <p className="text-sm text-gray-600">
                                                {new Date(orderDetail.accepted_at).toLocaleString('zh-TW')}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {(orderDetail.status === 'in_progress' || orderDetail.status === 'completed') && (
                                    <div className="flex items-start">
                                        <div className="mr-3 relative">
                                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">
                                                3
                                            </div>
                                            {orderDetail.status === 'completed' && (
                                                <div className="w-0.5 h-14 bg-gray-300 absolute left-1/2 top-6 transform -translate-x-1/2"></div>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium">行程開始</p>
                                            {orderDetail.started_at && (
                                                <p className="text-sm text-gray-600">
                                                    {new Date(orderDetail.started_at).toLocaleString('zh-TW')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {orderDetail.status === 'completed' && (
                                    <div className="flex items-start">
                                        <div className="mr-3">
                                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm">
                                                4
                                            </div>
                                        </div>
                                        <div>
                                            <p className="font-medium">行程完成</p>
                                            {orderDetail.completed_at && (
                                                <p className="text-sm text-gray-600">
                                                    {new Date(orderDetail.completed_at).toLocaleString('zh-TW')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 其他操作 */}
                    {orderDetail.status === 'accepted' && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold mb-3">其他操作</h4>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setShowCancelModal(true)}
                                    className="w-full border border-red-500 text-red-500 py-2 rounded-md font-medium"
                                >
                                    取消訂單
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 付款資訊 */}
                    {orderDetail.status === 'completed' && (
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <h4 className="font-bold mb-3">付款資訊</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">付款方式</span>
                                    <span className="font-medium">{
                                        {
                                            'credit_card': '信用卡',
                                            'cash': '現金',
                                            'line_pay': 'LINE Pay',
                                            'apple_pay': 'Apple Pay'
                                        }[orderDetail.payment_method] || orderDetail.payment_method
                                    }</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">付款狀態</span>
                                    <span className={
                                        orderDetail.payment_status === 'paid'
                                            ? 'text-green-600 font-medium'
                                            : 'text-red-600 font-medium'
                                    }>{
                                            {
                                                'pending': '待付款',
                                                'paid': '已付款',
                                                'failed': '付款失敗',
                                                'refunded': '已退款'
                                            }[orderDetail.payment_status] || orderDetail.payment_status
                                        }</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 手動派單 Modal */}
            {showAssignModal && (
                <ManualAssignmentModal
                    orderId={orderId}
                    orderDetail={orderDetail}
                    nearbyDrivers={nearbyDrivers}
                    onAssign={handleManualAssignment}
                    onClose={() => setShowAssignModal(false)}
                />
            )}

            {/* 取消訂單 Modal */}
            {showCancelModal && (
                <CancelOrderModal
                    onCancel={handleCancelOrder}
                    onClose={() => setShowCancelModal(false)}
                />
            )}
        </div>
    );
};

export default OrderDetailPage;